import React from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaLightbulb, FaRoad, FaTools, FaCheckCircle, FaClipboardCheck } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const ITConsulting = ({ market }) => {
  const b = getBrand(market);
  const ctaLink = market === 'lu' ? '/services/assessment' : '/contact';

  // 4 services → 2×2
  const services = [
    {
      icon: <FaChartLine size={32} color={b.accent} />,
      title: 'IT Strategy & Planning',
      description: 'Align technology with your business goals and create a roadmap for growth.',
      features: ['Technology assessment', 'Strategic roadmap development', 'Budget planning & ROI analysis', 'Vendor selection & evaluation']
    },
    {
      icon: <FaLightbulb size={32} color={b.accent} />,
      title: 'Digital Transformation',
      description: 'Modernise operations and leverage technology to stay competitive.',
      features: ['Process automation opportunities', 'Cloud migration strategy', 'Legacy system modernisation', 'Change management support']
    },
    {
      icon: <FaRoad size={32} color={b.accent} />,
      title: 'Infrastructure Assessment',
      description: 'Evaluate your current IT environment and identify improvement opportunities.',
      features: ['Network & security audit', 'Performance optimisation', 'Scalability planning', 'Disaster recovery readiness']
    },
    {
      icon: <FaTools size={32} color={b.accent} />,
      title: 'Technology Advisory',
      description: 'Expert guidance on technology decisions and implementations.',
      features: ['Architecture design', 'Best practices implementation', 'Compliance & governance', 'Risk assessment & mitigation']
    }
  ];

  // 6 benefits → 3-col
  const benefits = [
    'Independent, vendor-neutral advice',
    'Reduce IT costs by 20–40%',
    'Avoid costly technology mistakes',
    'Accelerate digital transformation',
    'Improve security and compliance posture',
    'Scale technology with business growth'
  ];

  // 4 approach steps → 2×2
  const approach = [
    { step: '01', title: 'Discovery',         description: 'Understand your business, challenges, and strategic objectives.' },
    { step: '02', title: 'Assessment',         description: 'Evaluate your current IT environment and identify gaps.' },
    { step: '03', title: 'Strategy',           description: 'Develop a comprehensive plan with priorities and timelines.' },
    { step: '04', title: 'Execution Support',  description: 'Guide implementation and ensure successful outcomes.' }
  ];

  // 3 who we help (bg only)
  const whoWeHelp = [
    { title: 'Growing Companies',      body: 'Scaling technology infrastructure to support business expansion and new markets.' },
    { title: 'Digital Transformation', body: 'Modernising legacy systems and embracing cloud technologies at pace.' },
    { title: 'M&A Integration',        body: 'Merging IT systems and ensuring seamless technology integration post-acquisition.' }
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="IT Consulting & Strategy"
        subtitle={market === 'lu'
          ? 'Expert technology guidance for Luxembourg businesses. Make smarter IT decisions and maximise ROI.'
          : 'Strategic IT advisory to align technology with your business goals and drive competitive advantage.'}
        ctaText={market === 'lu' ? 'Start with Free Assessment' : 'Schedule Consultation'}
        ctaLink={ctaLink}
        icon={<FaClipboardCheck color="#1A237E" />}
      />

      {/* ── CONSULTING SERVICES — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>What We Do</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our consulting services
            </h2>
          </div>
          <div className="grid-4">
            {services.map((svc, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px' }}
              >
                <div style={{ marginBottom: '16px' }}>{svc.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{svc.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '16px' }}>{svc.description}</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {svc.features.map((f, j) => (
                    <li key={j} style={{ display: 'flex', gap: '8px', fontSize: b.fz.xs, color: b.textMuted, alignItems: 'flex-start' }}>
                      <span style={{ color: b.accent, flexShrink: 0 }}>→</span> {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS — 6 → 3-col ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Business Value</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Why choose IT consulting?
            </h2>
          </div>
          <div className="grid-6">
            {benefits.map((benefit, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPROACH — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>How We Work</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our consulting approach
            </h2>
          </div>
          <div className="grid-4">
            {approach.map((phase, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: i * 0.07 }} viewport={{ once: true }}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center' }}
              >
                <div className="process-step-num" style={{ background: b.stepBg, border: b.stepBorder, color: b.stepColor }}>
                  {phase.step}
                </div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{phase.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted }}>{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE HELP (bg market, 3-col) ── */}
      {market === 'bg' && (
        <section style={{ background: b.bg, padding: b.secPad }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span className="eyebrow" style={{ color: b.eyebrowColor }}>Who We Help</span>
              <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
                Who we work with
              </h2>
            </div>
            <div className="grid-3">
              {whoWeHelp.map((item, i) => (
                <div key={i} style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '28px 24px', borderLeft: `3px solid ${b.accent}` }}>
                  <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{item.title}</h3>
                  <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready to optimise your IT strategy?"
        body={market === 'lu'
          ? 'Get a free IT assessment and discover opportunities to improve efficiency and reduce costs.'
          : "Let's discuss your technology challenges and create a strategic plan for success."}
        ctaText="Get Started"
        ctaLink={ctaLink}
      />
    </div>
  );
};

export default ITConsulting;
